import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useShareConversationGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      conversationId, 
      groupId 
    }: { 
      conversationId: string; 
      groupId: string; 
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Verificar se já existe compartilhamento
      const { data: existing } = await supabase
        .from('conversation_group_shares')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('group_id', groupId)
        .maybeSingle();

      if (existing) {
        throw new Error('Esta conversa já está compartilhada com este grupo');
      }

      // Inserir compartilhamento
      const { error } = await supabase
        .from('conversation_group_shares')
        .insert({
          conversation_id: conversationId,
          group_id: groupId,
          shared_by_user_id: user.id,
          permission_level: 'view',
        });

      if (error) throw error;

      // Buscar dados da conversa
      const { data: conversationData } = await supabase
        .from('legal_conversations')
        .select('title')
        .eq('id', conversationId)
        .single();

      // Buscar perfil do remetente
      const { data: senderProfile } = await supabase
        .from('users')
        .select('display_name')
        .eq('id', user.id)
        .single();

      const senderName = senderProfile?.display_name || 'Alguém';
      const conversationTitle = conversationData?.title || 'uma conversa';

      // Buscar todos os membros do grupo
      const { data: groupMembers } = await supabase
        .from('sharing_group_members')
        .select('user_id')
        .eq('group_id', groupId);

      if (groupMembers && groupMembers.length > 0) {
        // Criar notificações para cada membro
        const notificationInserts = groupMembers.map(member => ({
          type: 'chat_shared',
          title: 'Nova conversa compartilhada via grupo',
          message: `${senderName} compartilhou "${conversationTitle}" com seu grupo`,
          target_user_id: member.user_id,
          sent_by: user.id,
          metadata: { 
            conversation_id: conversationId,
            group_id: groupId,
            shared_via: 'group'
          },
        }));

        const { error: notifError } = await supabase
          .from('notifications')
          .insert(notificationInserts);

        if (notifError) {
          console.error('Erro ao criar notificações:', notifError);
        }

        // Registrar atividade
        await supabase.from('firm_activity_log').insert({
          firm_id: '00000000-0000-0000-0000-000000000000',
          actor_id: user.id,
          event_type: 'chat_shared_group',
          resource_type: 'conversation',
          resource_id: conversationId,
          metadata: { 
            group_id: groupId,
            members_notified: groupMembers.length
          }
        });
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shared-conversation-users'] });
      queryClient.invalidateQueries({ queryKey: ['shared-conversation-groups', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['global-share-count'] });
      toast.success('Conversa compartilhada com o grupo!');
    },
    onError: (error: Error) => {
      if (error.message.includes('já está compartilhada')) {
        toast.error('Esta conversa já está compartilhada com este grupo');
      } else {
        toast.error('Erro ao compartilhar com o grupo');
      }
    },
  });
}
